import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getClassName } from '@perpay-web/utils/domUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './CardApplicationUnfreeze.scss';

/**
 * Component for unfreezing credit file during card application
 * @param {Object} props - Component props
 * @param {Function} [props.onClickDone] - Callback when dashboard button is clicked
 * @param {Function} [props.onClickTryAgain] - Callback when try again button is clicked
 * @param {string} [props.dashboardURL] - URL for dashboard navigation
 * @param {boolean} [props.isPHO] - Whether this is a PHO application
 * @param {boolean} [props.isLoading] - Loading state for try again button
 * @param {boolean} [props.showRetryMessage] - Whether to show retry error message
 * @returns {JSX.Element} Component JSX
 */
const CardApplicationUnfreeze = ({
    onClickDone = noop,
    onClickTryAgain = noop,
    dashboardURL = paths.dashboard.path,
    isPHO = false,
    isLoading = false,
    showRetryMessage = false,
}) => {
    const retryMessage =
        "Your credit file still appears frozen. If you've already lifted the freeze, Experian and Clarity may take a few minutes to update. Please try again shortly.";

    return (
        <FocusLayout className={styles.container}>
            <FocusContent className={styles.content}>
                <Stack className={styles.header}>
                    <div className={styles.title}>
                        Your credit file is frozen
                    </div>
                    <div className={styles.copy}>
                        Placing a security freeze on your credit file is a great
                        way to prevent fraud and protect your credit! However,
                        we are unable to process your application with a freeze
                        in place.
                    </div>
                </Stack>

                <div className={styles.copy}>
                    <div>
                        <strong>
                            To proceed and unfreeze your credit please:
                        </strong>
                    </div>
                    <ul className={styles['steps-list']}>
                        <li>
                            Go to <strong>both</strong>{' '}
                            <CustomLink
                                href='https://www.clarityservices.com/'
                                target='_blank'
                            >
                                www.clarityservices.com
                            </CustomLink>{' '}
                            and{' '}
                            <CustomLink
                                href='https://www.experian.com/'
                                target='_blank'
                            >
                                www.experian.com
                            </CustomLink>
                        </li>
                        <li>Remove the security freeze on your account</li>
                        <li>
                            Click &ldquo;Try Again&rdquo; to reprocess your
                            application
                        </li>
                    </ul>
                </div>
                <div className={styles.copy}>
                    After your application is reprocessed, feel free to freeze
                    your credit file again!
                </div>
                {showRetryMessage ? (
                    <div
                        className={getClassName(
                            styles.copy,
                            styles['retry-message'],
                        )}
                    >
                        {retryMessage}
                    </div>
                ) : null}
            </FocusContent>
            <FocusFooter>
                <div className={styles['footer-buttons']}>
                    <CardCta
                        Component={CustomLink}
                        mode='dark-inverse'
                        onClick={onClickDone}
                        href={`${dashboardURL}${isPHO ? '?PHO_complete=true' : ''}`}
                        className={getClassName(
                            styles['footer-button'],
                            styles['dashboard-button'],
                        )}
                    >
                        Dashboard
                    </CardCta>
                    <CardCta
                        Component={CustomButton}
                        onClick={onClickTryAgain}
                        className={styles['footer-button']}
                        loading={isLoading}
                    >
                        Try Again
                    </CardCta>
                </div>
            </FocusFooter>
        </FocusLayout>
    );
};

export default CardApplicationUnfreeze;
